import React, { useContext, useEffect, useState } from 'react';
import t, { useIntl } from '@jetshop/intl';
import { styled } from 'linaria/react';
import { css, cx } from 'linaria';
import Modal from 'react-modal';
import { ReactComponent as Cross26Icon } from '../../../../svg/Cross26.svg';
import { useFavoriteStore } from '../../../../contexts/FavoriteStoreContext';
import { theme } from '../../../Theme';
import { ElevateCheckbox } from './ElevateCheckbox';
import { useHistory } from 'react-router-dom';
import { useElevate } from '../../ElevateContext';
import ChannelContext from '@jetshop/core/components/ChannelContext';
const checkboxFacetListStyle = css`
  background: #f3f3f3;
`;

const TriggerButton = styled('button')`
  background: none;
  border: none;
  padding: 0;
  margin-left: 6px;
  text-decoration: underline;
  font-size: 14px;
  cursor: pointer;
  color: inherit;
  text-transform: uppercase;
  font-weight: 600;
`;

const modalStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    // Drawer flyout is z-index 99999 — sit above it
    zIndex: 100000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  content: {
    position: 'relative',
    inset: 'auto',
    top: 'auto',
    left: 'auto',
    right: 'auto',
    bottom: 'auto',
    border: 'none',
    background: '#fff',
    borderRadius: '8px',
    outline: 'none',
    padding: 0,
    width: 'min(420px, calc(100vw - 32px))',
    maxHeight: 'min(80vh, 640px)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.2)',
    zIndex: 100001
  }
};

const ModalBody = styled('div')`
  display: flex;
  flex-direction: column;
  max-height: min(80vh, 640px);
`;

const ModalHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 6px 12px 20px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;

  h3 {
    font-size: 18px;
    margin: 0;
    font-weight: 600;
  }
`;

const CloseIconButton = styled('button')`
  border-radius: 50%;
  border: none;
  background: transparent;
  width: fit-content;
  height: 36px;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;

  &:hover,
  &:active {
    background: #e6e6e6;
  }

  svg {
    margin-left: 4px;
    use {
      fill: black;
    }

    width: 22px;
    height: 22px;
  }
`;

const ModalFooter = styled('div')`
  flex-shrink: 0;
  padding: 12px 20px 20px;
  border-top: 1px solid #eee;

  button {
    width: 100%;
    height: 48px;
    background: #e6e6e6;
    color: #18181b;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    cursor: pointer;

    &:hover {
      background: #d9d9d9;
    }
  }
`;

const ModalList = styled('div')`
  /* padding: 0 20px; */
  padding: 0 0px 2rem 8px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  flex: 1;
  min-height: 0;
  scrollbar-width: thin;
  scrollbar-color: ${theme.colors.accent} transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.accent};
    border-radius: 4px;
  }
`;

const SectionLabel = styled('div')`
  margin: 15px 1rem 0px;
  font-weight: 400;
  font-size: 14px;
  padding-bottom: 0px;
`;

const StoreRow = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin: 0 1rem;
  padding: 12px 0;
  border-bottom: 1px solid #d2d2d2;

  .name {
    font-weight: 700;
    font-size: 16px;
  }

  &.selected {
    background: ${theme.colors.clubhooksgreen};
    border-bottom: none;
    border-radius: 6px;
    padding: 12px;
    margin: 4px 0 8px;

    .name {
      color: #fff;
    }

    button {
      background: #fff;
      color: ${theme.colors.clubhooksgreen};

      &:hover {
        background: #f3f3f3;
      }
    }
  }
`;

const ChooseButton = styled('button')`
  background: ${theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #333;
  }
`;

const EmptyState = styled('p')`
  margin: 1rem 0;
  font-size: 14px;
  color: ${theme.colors.grey};
`;

export const CUSTOM_STORE_FACET_KEYS = {
  ONLINE: 'c.online',
  STORE: 'c.store'
};

/**
 * Store picker for Elevate stock filtering.
 * onSelectStore({ id, name }) — wire URL params / Elevate `stores` later.
 */
export const CustomStoreFacet = ({
  onSelectStore,
  defaultSelected = false
}) => {
  const [open, setOpen] = useState(false);
  const { selectedChannel } = useContext(ChannelContext);
  const history = useHistory();
  const { favoriteStore, getAllStores, storesLoading } = useFavoriteStore();
  const translate = useIntl();
  const { objectToQueryString, queryStringToObject, listFacetStoreId } =
    useElevate();
  const stores = [...(getAllStores() || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const findStoreById = id => {
    return stores.find(store => store.id === parseInt(id));
  };

  const preSelectedStore =
    findStoreById(listFacetStoreId) ?? favoriteStore ?? null;

  const [selectedStore, setSelectedStore] = useState(preSelectedStore);

  const getActiveCustomStoreFacets = locationSearch => {
    const currentParams = queryStringToObject(locationSearch);

    return Object.values(CUSTOM_STORE_FACET_KEYS).reduce((acc, key) => {
      const value = currentParams[key];
      acc[key] =
        Object.prototype.hasOwnProperty.call(currentParams, key) &&
        value !== false &&
        value !== 'false';
      return acc;
    }, {});
  };

  const activeCustomStoreFacets = getActiveCustomStoreFacets(
    history.location.search
  );

  // Sync c.online from Elevate onlyInStock.selected when URL has no decision yet
  useEffect(() => {
    if (!defaultSelected) return;

    const params = queryStringToObject(history.location.search);
    if (
      Object.prototype.hasOwnProperty.call(
        params,
        CUSTOM_STORE_FACET_KEYS.ONLINE
      )
    ) {
      return;
    }

    history.replace({
      hash: history.location.hash,
      pathname: history.location.pathname,
      search: objectToQueryString({
        ...params,
        [CUSTOM_STORE_FACET_KEYS.ONLINE]: true
      })
    });
  }, [defaultSelected]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => setOpen(false);

  const editCustomStoreFacets = (key, value) => {
    const currentParams = queryStringToObject(history.location.search);

    currentParams[key] = value;
    delete currentParams.page;

    history.push({
      pathname: history.location.pathname,
      search: objectToQueryString(currentParams),
      hash: history.location.hash
    });
  };

  const handleToggleStockFacet = (key, value) => {
    const currentParams = queryStringToObject(history.location.search);
    const nextParams = { ...currentParams };
    const isOn =
      Object.prototype.hasOwnProperty.call(nextParams, key) &&
      nextParams[key] !== false &&
      nextParams[key] !== 'false';

    if (key === CUSTOM_STORE_FACET_KEYS.ONLINE) {
      // Keep the key so Elevate defaults don't turn it back on after uncheck
      nextParams[key] = isOn ? false : value;
    } else if (isOn) {
      delete nextParams[key];
    } else {
      nextParams[key] = value;
    }

    delete nextParams.page;

    history.push({
      pathname: history.location.pathname,
      search: objectToQueryString(nextParams),
      hash: history.location.hash
    });
  };

  const handleSelect = store => {
    setSelectedStore(store);

    if (!store) {
      const currentParams = queryStringToObject(history.location.search);
      const nextParams = { ...currentParams };
      delete nextParams[CUSTOM_STORE_FACET_KEYS.STORE];

      history.push({
        pathname: history.location.pathname,
        search: objectToQueryString(nextParams),
        hash: history.location.hash
      });
    } else {
      const storeKey = store.id;
      editCustomStoreFacets(CUSTOM_STORE_FACET_KEYS.STORE, storeKey);
    }
    close();
  };

  return (
    <>
      <ul
        className={cx(
          'custom-store-checkbox-facet-list',
          checkboxFacetListStyle
        )}
      >
        <li>
          <ElevateCheckbox
            label={translate('In stock online')}
            checked={activeCustomStoreFacets?.[CUSTOM_STORE_FACET_KEYS.ONLINE]}
            onChange={() =>
              handleToggleStockFacet(CUSTOM_STORE_FACET_KEYS.ONLINE, true)
            }
          />
        </li>
        {!['EU', 'NL'].includes(selectedChannel.name) && (
          <li>
            <ElevateCheckbox
              label={
                <div>
                  {selectedStore?.name
                    ? `${
                        translate('In stock at store') +
                        ` ${selectedStore?.name}`
                      }`
                    : translate('In stock at store')}
                  <TriggerButton
                    type="button"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setOpen(true);
                    }}
                  >
                    {translate('Select store')}
                  </TriggerButton>
                </div>
              }
              checked={activeCustomStoreFacets?.[CUSTOM_STORE_FACET_KEYS.STORE]}
              onChange={() => {
                if (!selectedStore?.id) {
                  setOpen(true);
                  return;
                }
                handleToggleStockFacet(
                  CUSTOM_STORE_FACET_KEYS.STORE,
                  selectedStore.id
                );
              }}
            />
          </li>
        )}
      </ul>

      <Modal
        isOpen={open}
        onRequestClose={close}
        style={modalStyles}
        contentLabel={translate('Select store')}
        ariaHideApp={false}
      >
        <ModalBody>
          <ModalHeader>
            <h3>{t('Select store')}</h3>
            <CloseIconButton
              type="button"
              onClick={close}
              aria-label={translate('Close')}
            >
              Stäng
              <Cross26Icon />
            </CloseIconButton>
          </ModalHeader>

          <ModalList>
            {storesLoading && <EmptyState>{t('Loading...')}</EmptyState>}

            {!storesLoading && stores.length === 0 && (
              <EmptyState>{t('No stores available')}</EmptyState>
            )}

            {!storesLoading && selectedStore?.name && (
              <>
                <SectionLabel>{t('Selected store')}</SectionLabel>
                <StoreRow className="selected">
                  <span className="name">{selectedStore?.name}</span>
                  <ChooseButton
                    type="button"
                    onClick={() => handleSelect(null)}
                  >
                    {t('Clear')}
                  </ChooseButton>
                </StoreRow>
              </>
            )}

            {!storesLoading && stores.length > 0 && (
              <>
                <SectionLabel>{t('All stores')}</SectionLabel>
                {stores
                  .filter(store => store.name !== selectedStore?.name)
                  .map(store => (
                    <StoreRow key={store.id || store.name}>
                      <span className="name">{store.name}</span>
                      <ChooseButton
                        type="button"
                        onClick={() => handleSelect(store)}
                      >
                        {t('Choose')}
                      </ChooseButton>
                    </StoreRow>
                  ))}
              </>
            )}
          </ModalList>
        </ModalBody>
      </Modal>
    </>
  );
};
